import { useState } from "react";
import { BookOpen, Printer, Tablet, ShieldCheck, Server, Wrench, LifeBuoy, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const SECTIONS = [
  { id: "prep", label: "Prepare the iPad", icon: Tablet },
  { id: "guided", label: "Guided Access", icon: ShieldCheck },
  { id: "fleet", label: "Single App Mode (fleets)", icon: Server },
  { id: "harden", label: "Extra hardening", icon: Wrench },
  { id: "cheat", label: "Manager cheat-sheet", icon: BookOpen },
  { id: "trouble", label: "Troubleshooting", icon: LifeBuoy },
];

function H({ id, icon: Icon, children }) {
  return (
    <h2 id={id} className="scroll-mt-24 flex items-center gap-2.5 text-xl font-bold font-admin-head text-slate-900 dark:text-white mt-10 mb-3">
      <span className="h-9 w-9 rounded-xl grid place-items-center text-white shrink-0" style={{ background: "var(--brand-primary, #3730A3)" }}><Icon className="h-4.5 w-4.5" /></span>
      {children}
    </h2>
  );
}
function Step({ n, children }) {
  return (
    <li className="flex gap-3">
      <span className="h-6 w-6 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-bold grid place-items-center shrink-0 mt-0.5">{n}</span>
      <span className="text-slate-700 dark:text-slate-300 leading-relaxed">{children}</span>
    </li>
  );
}
function Tip({ children }) {
  return <div className="rounded-lg border-l-4 border-indigo-400 bg-indigo-50/60 dark:bg-indigo-950/30 px-4 py-3 text-sm text-indigo-900 dark:text-indigo-200 my-4">{children}</div>;
}

export default function UserGuide() {
  const [active, setActive] = useState("prep");
  const jump = (id) => { setActive(id); document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  return (
    <div className="p-6 lg:p-8" data-testid="user-guide-page">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-2xl grid place-items-center text-white" style={{ background: "var(--brand-primary, #3730A3)" }}><BookOpen className="h-5 w-5" /></div>
          <div>
            <h1 className="font-admin-head text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">User Guide</h1>
            <p className="text-slate-500 mt-0.5">How to lock an iPad to the The Good Box kiosk so customers can't leave the app.</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => window.print()} data-testid="user-guide-print"><Printer className="h-4 w-4 mr-1.5" /> Print</Button>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-8">
        <aside className="lg:sticky lg:top-6 self-start hidden lg:block">
          <nav className="space-y-1" data-testid="user-guide-toc">
            {SECTIONS.map((s) => (
              <button key={s.id} data-testid={`guide-toc-${s.id}`} onClick={() => jump(s.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${active === s.id ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
                <s.icon className="h-4 w-4 shrink-0" /> <span className="truncate">{s.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        <article className="max-w-3xl">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              This guide locks an iPad to a single kiosk so shoppers can't browse other sites or change settings. Two options:
            </p>
            <ul className="mt-3 space-y-1.5 text-sm text-slate-600 dark:text-slate-300">
              <li className="flex items-center gap-2"><ChevronRight className="h-4 w-4 text-indigo-500" /> <b>Guided Access</b> — fastest & free, per device. Best for 1–10 iPads.</li>
              <li className="flex items-center gap-2"><ChevronRight className="h-4 w-4 text-indigo-500" /> <b>Single App Mode</b> (MDM / Apple Configurator) — auto-locks on power-up, survives reboots. Best for fleets.</li>
            </ul>
            <p className="mt-3 text-xs text-slate-400">You'll need: iPadOS 15+, a dedicated Apple ID, and the kiosk URL for the unit (e.g. <span className="font-mono-x">https://vend.thegoodbox.com/k/KIOSK-004</span> — find the code in <b>Sites &amp; Kiosks</b>).</p>
          </div>

          <H id="prep" icon={Tablet}>1. Prepare the iPad (all methods)</H>
          <ol className="space-y-2.5">
            <Step n={1}><b>Settings → Wi‑Fi</b>: connect to reliable, always-on Wi‑Fi.</Step>
            <Step n={2}><b>Settings → Display &amp; Brightness → Auto‑Lock → Never</b> so the screen never sleeps.</Step>
            <Step n={3}><b>Settings → Notifications</b>: turn Off for all apps so nothing pops over the kiosk.</Step>
            <Step n={4}><b>Settings → Safari</b>: AutoFill Off, Block Pop‑ups On.</Step>
            <Step n={5}>Open <b>Safari</b>, go to the kiosk URL, then <b>Share → Add to Home Screen</b> and name it "The Good Box".</Step>
            <Step n={6}>Always launch the kiosk from that <b>Home Screen icon</b> — it opens full-screen with no address bar.</Step>
          </ol>
          <Tip>Launching from the Home Screen icon (a PWA) hides the Safari address bar and keyboard toolbar, giving a true full-screen kiosk.</Tip>

          <H id="guided" icon={ShieldCheck}>2. Guided Access (single iPad, free)</H>
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-2">Enable it once</p>
          <ol className="space-y-2.5">
            <Step n={1}><b>Settings → Accessibility → Guided Access → On</b>.</Step>
            <Step n={2}><b>Passcode Settings → Set Guided Access Passcode</b> — a manager-only code (optionally enable Face/Touch ID).</Step>
          </ol>
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-4 mb-2">Start a locked session</p>
          <ol className="space-y-2.5">
            <Step n={1}>Open the <b>The Good Box</b> Home Screen icon.</Step>
            <Step n={2}><b>Triple-click</b> the top button (or Home button on older iPads).</Step>
            <Step n={3}>Tap <b>Options</b> and turn OFF Sleep/Wake, Volume and Motion. Leave <b>Touch ON</b> <u>and Keyboards ON</u> — customers need the on-screen keyboard to type their email for a receipt. Turn Time Limit off.</Step>
            <Step n={4}>Tap <b>Start</b>. The iPad is now locked to the kiosk.</Step>
          </ol>
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-4 mb-2">End a session (staff only)</p>
          <p className="text-sm text-slate-700 dark:text-slate-300">Triple-click the top/Home button, enter the passcode, tap <b>End</b>.</p>
          <Tip>Guided Access does <b>not</b> auto-resume after a reboot — staff must reopen the app and triple-click again. For unattended sites use Single App Mode below.</Tip>

          <H id="fleet" icon={Server}>3. Single App Mode (fleets, auto-resumes)</H>
          <p className="text-sm text-slate-700 dark:text-slate-300 mb-2">Locks the iPad to one app permanently and survives reboots. Requires the device to be <b>supervised</b>. This whole section is <b>optional</b> — for a single iPad, <b>Guided Access (§2 above)</b> is all you need.</p>
          <div className="rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50/70 dark:bg-amber-950/20 px-3 py-2.5 text-sm text-amber-800 dark:text-amber-200 my-3" data-testid="no-mac-note">
            <b>No Mac?</b> Skip Apple Configurator entirely — it's Mac-only. Use <b>Guided Access (§2)</b> for 1–10 iPads (no Mac, no MDM), or an <b>MDM</b> below (Jamf / Mosyle / Kandji / Intune) for larger fleets, which also needs no Mac.
          </div>
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-3 mb-2">Apple Configurator (free, needs a Mac — optional)</p>
          <ol className="space-y-2.5">
            <Step n={1}>Install <b>Apple Configurator</b> on the Mac; connect the iPad.</Step>
            <Step n={2}><b>Prepare</b> the device and choose <b>Supervise</b> (this erases it — do it before go-live).</Step>
            <Step n={3}>Select the device → <b>Advanced → Start Single App Mode</b> → pick the The Good Box web-clip → <b>Apply</b>.</Step>
          </ol>
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-4 mb-2">MDM (Jamf, Mosyle, Kandji, Intune) — recommended</p>
          <ol className="space-y-2.5">
            <Step n={1}>Enrol the iPad as a <b>supervised</b> device (via Apple Business Manager for zero-touch).</Step>
            <Step n={2}>Create a <b>Single App Mode / Kiosk</b> payload targeting the kiosk web-clip, plus a <b>Web Clip</b> payload with the kiosk URL.</Step>
            <Step n={3}>Add restrictions: block app removal, disable AirDrop, enforce Auto-Lock = Never and auto-restart on power loss.</Step>
            <Step n={4}>Assign to your "Kiosks" device group — new iPads then self-configure on unboxing.</Step>
          </ol>

          <H id="harden" icon={Wrench}>4. Extra hardening</H>
          <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-300 list-disc pl-5">
            <li><b>Restrict Safari to one site:</b> Settings → Screen Time → Content &amp; Privacy → Content Restrictions → Web Content → <b>Allowed Websites</b> → add <span className="font-mono-x">vend.thegoodbox.com</span>. Blocks every other URL.</li>
            <li><b>Physical:</b> use a locking floor/counter enclosure that covers the Home button and ports; route power through the stand.</li>
            <li><b>Auto power-on:</b> keep the iPad plugged in and enable auto-restart in your MDM so a power blip returns to the kiosk.</li>
          </ul>

          <H id="cheat" icon={BookOpen}>5. Manager cheat-sheet</H>
          <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
            <table className="w-full text-sm">
              <thead><tr className="text-left bg-slate-50 dark:bg-slate-900 text-slate-500"><th className="px-4 py-2 font-medium">Action</th><th className="px-4 py-2 font-medium">How</th></tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                <tr><td className="px-4 py-2.5 font-medium">Start lock</td><td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">Open kiosk → triple-click top button → Start</td></tr>
                <tr><td className="px-4 py-2.5 font-medium">End lock</td><td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">Triple-click → enter passcode → End</td></tr>
                <tr><td className="px-4 py-2.5 font-medium">Kiosk froze</td><td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">End Guided Access, close &amp; reopen the app, re-start it</td></tr>
                <tr><td className="px-4 py-2.5 font-medium">Change kiosk shown</td><td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">Open Safari, go to the new /k/CODE URL, re-add to Home Screen</td></tr>
              </tbody>
            </table>
          </div>

          <H id="trouble" icon={LifeBuoy}>6. Troubleshooting</H>
          <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-300 list-disc pl-5">
            <li><b>Address bar / keyboard shows</b> → you launched from Safari, not the Home Screen icon. Re-add to Home Screen and launch from there.</li>
            <li><b>Kiosk shows "offline" in Admin</b> → the iPad lost Wi‑Fi or the tab closed. Reconnect and reopen; heartbeat resumes within ~2 minutes.</li>
            <li><b>Customers can swipe to other apps</b> → Guided Access / Single App Mode isn't active. Re-start it.</li>
            <li><b>Forgot the Guided Access passcode</b> → end with Face/Touch ID if enabled, otherwise erase &amp; re-set up the device.</li>
          </ul>

          <div className="mt-10 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 flex items-center gap-3">
            <LifeBuoy className="h-5 w-5 text-slate-400 shrink-0" />
            <p className="text-sm text-slate-600 dark:text-slate-300">Still stuck? Use <b>Contact Support</b> at the bottom of any admin page and we'll help.</p>
          </div>
        </article>
      </div>
    </div>
  );
}
